import { JSX, useContext, useEffect, useState } from "react";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { AdvancedExclusionsContext } from "@app/projects/projection-provider/advanced-exclusions/AdvancedExclusionsContext";
import { ProjectionProviderIdentifier } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { ProviderExclusionReason } from "@app/projects/results/projection-provider";
import ProjectService from "@app/projects/projectService";
import { PhoneNumberFormatHelper } from "@app/projects/projection-provider/PhoneNumberFormatHelper";
import PageSection from "@app/shared/PageSection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { UserContext } from "@app/user/UserContext";
import { QESCard } from "@app/shared/qes-standards/Card";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface AdvancedExclusionsConfirmProps {
  pageIsValid: (flag: boolean) => void;
}

const AdvancedExclusionsConfirm = (props: AdvancedExclusionsConfirmProps): JSX.Element => {
  const wizardContext = useContext(AdvancedExclusionsContext);
  const user = useContext(UserContext);
  const [reviewProjects, setReviewProjects] = useState<ReviewProjectsTableData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reasonsForExcluding, setReasonsForExcluding] = useState<ProviderExclusionReason[]>([]);
  const projectService = new ProjectService();
  const phoneNumberFormatHelper = new PhoneNumberFormatHelper();

  const providersWithTinOrNPI = wizardContext.selectedExclusionProviders.filter((provider) => !!provider.npi || !!provider.tin)

  const showNpi = providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.npi);
  const tinViewer = user.roles.includes(Roles.TIN_VIEWER);
  const showTin = tinViewer && providersWithTinOrNPI.length > 0 && providersWithTinOrNPI.every((provider) => !!provider.tin);

  const reviewColumnConfig: ColumnConfig = {
    projectName: {
      displayText: `Projects (${reviewProjects.length})`,
      testId: "project-name",
    },
  };

  interface ReviewProjectsTableData {
    [wafflesKeyColumnName]: string;
    projectName: string;
  }

  useEffect(() => {
    setIsLoading(true);

    const tabledata: ReviewProjectsTableData[] = wizardContext.projects.filter((project) => wizardContext.selectedProjectIds.includes(project.projectId.toString())).map((project) => {
      return {
        /* oxlint-disable sort-keys */
        key: project.projectId.toString(),
        projectName: project.projectName,
        /* oxlint-enable sort-keys */
      };
    });

    projectService.getProviderExclusionReasons(wizardContext.projects[0].projectId).then(
      (response) => setReasonsForExcluding(response.map(item => item))
    );

    setReviewProjects(tabledata);;
    setIsLoading(false);
  }, []);

  useEffect(() => {
    props.pageIsValid(wizardContext.exclusionReason != "" && (wizardContext.exclusionReason != "Other" || wizardContext.comment != "") && (wizardContext.phone.length === 0 || wizardContext.phone.length === 10));
  }, [wizardContext.exclusionReason, wizardContext.comment, wizardContext.phone]);

  return (
    <PageSection>
      <div className="container">
        <div className="mb-3">
          <QESCard title="Details" classNames="h-100">
            <div className="d-flex justify-content-between align-items-top">
              <div className="d-flex flex-column">
                <div className="row">
                  <QESCheckbox
                    checked={wizardContext.allSpecialties}
                    label="All Specialties"
                    disabled={showNpi === false}
                    onChange={(e) => wizardContext.setAllSpecialties(e.target.checked)}
                    testId="advanced-exclusions-wizard__all-specialties"
                  />
                </div>
                <div className="row mb-2">
                  <QESCheckbox
                    label="All Locations"
                    checked={wizardContext.allLocations}
                    disabled={showNpi === false || wizardContext.phone.length > 0}
                    onChange={(e) => {
                      wizardContext.setAllLocations(e.target.checked);
                      if (e.target.checked) {
                        // Phone number must be blank if All Locations is true.
                        wizardContext.setPhone("");
                      }
                    }}
                    testId="advanced-exclusions-wizard__all-locations"
                  />
                </div>
                <div className="row">
                  <label className="col-12 label-s d-block mb-2" htmlFor="reason">Recording Criteria:</label>
                </div>
                <div className="row">
                  <div className="col-12 ms-4">
                    {showNpi && (<div>
                      <QESRadioButton
                        label="NPI"
                        checked={wizardContext.exclusionsType === "npi"}
                        name="identifier"
                        onChange={(e) => wizardContext.setExclusionsType(e.target.value as ProjectionProviderIdentifier)}
                        value="npi"
                        testId="advanced-exclusions-wizard__by-npi"
                      />
                    </div>)}

                    {showTin && (<div>
                      <QESRadioButton
                        label="TIN"
                        checked={wizardContext.exclusionsType === "tin"}
                        name="identifier"
                        onChange={(e) => wizardContext.setExclusionsType(e.target.value as ProjectionProviderIdentifier)}
                        value="tin"
                        testId="advanced-exclusions-wizard__by-tin"
                      />
                    </div>)}

                    {showNpi && showTin && (<div>
                      <QESRadioButton
                        label="NPI and TIN"
                        checked={wizardContext.exclusionsType === "npitin"}
                        name="identifier"
                        onChange={(e) => wizardContext.setExclusionsType(e.target.value as ProjectionProviderIdentifier)}
                        value="npitin"
                        testId="advanced-exclusions-wizard__by-npi-and-tin"
                      />
                    </div>)}
                  </div>
                </div>
                <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
                  <div className="row mb-3">
                        <div>
                          <QESCheckbox
                            label="Run Projection Analysis Later"
                            testId="advanced-exclusions-wizard__run-later"
                            checked={!wizardContext.runProjectionOnSubmit}
                            onChange={(e) => {
                              wizardContext.setRunProjectionOnSubmit(!e.target.checked);
                            }}
                          />
                        </div>
                  </div>
                </RoleRestricted>
              </div>
              <div className="col-6">
                <div className="row mb-3">
                  <QESSelect
                    label="Exclusion Reason"
                    containerClassName="col-12"
                    testId="advanced-exclusions-wizard__reason"
                    onChange={e => wizardContext.setExclusionReason(e.target.value)}
                    value={wizardContext.exclusionReason}
                    required={true}
                    isValid={!!wizardContext.exclusionReason}
                    placeholder={`Reason for exclusion.`}
                  >
                    <>
                      {reasonsForExcluding.map((o, i) => (
                        <option key={i + 1} value={o.reason}>
                          {
                            `${o.reason}${o.isFacilityBasedISNP ? '**'
                              : o.validReasonForMAExceptionRequestForm
                                ? '*' : ''}`
                          }
                        </option>
                      ))}
                    </>
                  </QESSelect>
                  <div className="col-12">
                    <p className="mt-1">* Designates a valid reason for MA Exception Request form. Invalid reasons will appear blank on CMS Exception Request form.</p>
                    <p className="mt-1">** Only facility-based I-SNPs may use as a valid reason.</p>
                  </div>
                </div>
                <div className="row">
                  <QESTextArea
                    containerClassName="col-12"
                    onChange={e => wizardContext.setComment(e.target.value)}
                    value={wizardContext.comment}
                    maxLength={1000}
                    label="Comment"
                    testId="advanced-exclusions-wizard__comment"
                    required={wizardContext.exclusionReason === "Other"}
                  />
                </div>
                <div className="row">

                  <QESTextInput
                    containerClassName="col-12"
                    label={<>Phone Number <TooltipWrapper position="right" tooltipWidth="500px" text={phoneNumberFormatHelper.getPhoneTooltipWrapperText()}><i className="fa fa-info-circle" /> </TooltipWrapper> </>}
                    placeholder="Phone Number"
                    errorText="Phone must have 10 digits."
                    testId="advanced-exclusions-wizard__phone"
                    disabled={wizardContext.selectedExclusionProviders.length !== 1 || wizardContext.allLocations}
                    value={phoneNumberFormatHelper.formatPhone(wizardContext.phone)}
                    unrequiredValidation={true}
                    isValid={wizardContext.phone.length == 0 || wizardContext.phone.length == 10}
                    onChange={e => {
                      const phone = phoneNumberFormatHelper.getNumbersFromString(e.target.value);
                      wizardContext.setPhone(phone);
                      if (phone.length > 0) {
                        wizardContext.setAllLocations(false);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </QESCard>
        </div>
        <div className="mb-2">
          <Waffles<ReviewProjectsTableData>
            columns={reviewColumnConfig}
            data={reviewProjects}
            fullWidth={true}
            loading={isLoading}
            testId="advanced-exclusions-wizard__review-projects"
          />
        </div>
      </div>
    </PageSection>
  );
}

export default AdvancedExclusionsConfirm;